/**
 * useWebPlacementDeploy Hook
 *
 * React hook for deploying web placements to GCS.
 * Handles async state and error management.
 *
 * Usage:
 * ```tsx
 * function DeployButton({ projectId, webPlacementId, userId }: Props) {
 *   const { deploy, isDeploying, error, clearError } = useWebPlacementDeploy();
 *
 *   const handleClick = async () => {
 *     clearError();
 *     const result = await deploy({ projectId, webPlacementId, userId });
 *
 *     if (result.success) {
 *       console.log('Published to:', result.webUrl);
 *     } else {
 *       console.error('Failed:', result.error);
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleClick} disabled={isDeploying}>
 *       {isDeploying ? 'Publishing...' : 'Publish'}
 *     </Button>
 *   );
 * }
 * ```
 *
 * @module webplacements/hooks
 */

import { useState, useCallback, useRef } from 'react';
import { WebPlacementDeployService } from '../services/WebPlacementDeployService';
import { WebPlacementDeployError } from '@modules/webplacements/types/webplacements.types';
import type {
  WebPlacementDeployParams,
  WebPlacementDeployResult,
} from '@modules/webplacements/types/webplacements.types';

/**
 * Result type for useWebPlacementDeploy hook
 */
export interface UseWebPlacementDeployResult {
  /**
   * Deploy a web placement to GCS
   *
   * @param params - Deploy parameters (projectId, webPlacementId, userId)
   * @returns Deploy result with success status, webUrl, and optional error
   */
  deploy: (params: WebPlacementDeployParams) => Promise<WebPlacementDeployResult>;

  /** Whether deployment is currently in progress */
  isDeploying: boolean;

  /** Last deployment error (null if no error) */
  error: Error | null;

  /** Last deployment result (null if not deployed yet) */
  lastResult: WebPlacementDeployResult | null;

  /** Clear error and result state */
  clearError: () => void;
}

/**
 * useWebPlacementDeploy Hook
 *
 * Provides async state management for web placement deployment.
 *
 * @returns Deploy function and state (isDeploying, error, lastResult, clearError)
 */
export function useWebPlacementDeploy(): UseWebPlacementDeployResult {
  // State
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastResult, setLastResult] = useState<WebPlacementDeployResult | null>(null);

  // Service ref (stable across renders)
  const serviceRef = useRef<WebPlacementDeployService | null>(null);

  // Get or create service instance
  const getService = useCallback(() => {
    if (!serviceRef.current) {
      serviceRef.current = new WebPlacementDeployService();
    }
    return serviceRef.current;
  }, []);

  // Deploy function
  const deploy = useCallback(
    async (params: WebPlacementDeployParams): Promise<WebPlacementDeployResult> => {
      setIsDeploying(true);
      setError(null);
      setLastResult(null);

      try {
        const service = getService();
        const result = await service.deploy(params);

        setLastResult(result);

        // If deployment failed with error message, set error state.
        // Typed error keeps the machine-readable code + healthcheck evidence
        // so consumers can react to a specific failure (e.g. privacy review).
        if (!result.success && result.error) {
          setError(new WebPlacementDeployError(result.error, result.errorCode, result.issues));
        }

        return result;
      } catch (e) {
        const deployError = e instanceof Error ? e : new Error(String(e));
        setError(deployError);

        const failResult: WebPlacementDeployResult = {
          success: false,
          webUrl: '',
          gcsFiles: [],
          error: deployError.message,
        };

        setLastResult(failResult);
        return failResult;
      } finally {
        setIsDeploying(false);
      }
    },
    [getService]
  );

  // Clear error and result
  const clearError = useCallback(() => {
    setError(null);
    setLastResult(null);
  }, []);

  return {
    deploy,
    isDeploying,
    error,
    lastResult,
    clearError,
  };
}
